<template>
  <div
    :class="{
      'no-frames': noFrames,
      'has-row-selection': selectableRow
    }"
    :style="{
      'max-height': `${maxHeight}px` || false
    }"
    class="datable-grid-wrapper"
    @scroll="scrollHandler"
  >
    <!-- CABEÇALHO -->
    <div
      v-if="showHeader"
      :class="[
        `datatable${componentKey}`,
        {
          scrolled: tableScrolled
        }
      ]"
      class="row thead no-gutters"
    >
      <!-- COLUNA DE SELEÇÃO -->
      <div
        v-if="selectableRow"
        :key="`datatable-${componentKey}-checkbox-all`"
        class="col th th--selection"
      >
        <InputCheckbox
          :checked="selectedRowsCount > 0"
          name="all"
          checkedIcon="minus"
          @change="selectAllRows"
        />
      </div>
      <!-- / COLUNA DE SELEÇÃO -->
      <!-- COLUNAS DINÂMICAS -->
      <div
        v-for="(key, i) in cols"
        :key="`datatable-${componentKey}-${key.alias}-${i}`"
        class="col th"
        :style="{
          'min-width': `${colsMinWidths ? colsMinWidths[key.name] : ''}`
        }"
        @click="sortBy(key.name)"
      >
        <!-- Se possuí checkboxes, atualiza o nome da primeira coluna com a quantidade de selecionados -->
        <span
          v-if="selectableRow && selectedRowsCount > 0 && i === 0"
          class="label"
          :title="key.alias"
          >{{ selectedRowsCount | pluralizer(selectedsRowsTerms) }}</span
        >
        <!-- senão mostra apenas o nome da coluna -->
        <span v-else class="label" :title="key.alias">{{ key.alias }}</span>

        <i
          v-if="sortOrders[key.name] > 0"
          class="icon icon-sort-up icon-12"
        ></i>
        <i
          v-else-if="sortOrders[key.name] < 0"
          class="icon icon-sort-down icon-12"
        ></i>
        <i v-else class="icon icon-sort icon-12"></i>
      </div>
      <!-- / COLUNAS DINÂMICAS -->
    </div>
    <!-- / CABEÇALHO -->
    <!-- CORPO -->
    <!-- LOADER -->
    <div v-if="isLoading" class="tbody">
      <!-- SKELETON -->
      <div class="skeleton-wrapper">
        <div v-for="j in 10" :key="`skeleton-row-${j}`" class="skeleton-row">
          <!-- COLUNA DE SELEÇÃO -->
          <div
            v-if="selectableRow"
            :key="`skeleton-col-checkbox-${j}`"
            class="skeleton-col skeleton-col--selection"
          >
            <Skeleton />
          </div>

          <div
            v-for="(key, i) in cols"
            :key="`skeleton-col-${key.name}-${i}`"
            :style="{
              'min-width': `${colsMinWidths ? colsMinWidths[key.name] : ''}`
            }"
            class="skeleton-col"
          >
            <Skeleton />
          </div>
        </div>
      </div>
    </div>
    <!-- LINHA DA TABELA -->
    <div v-else-if="filteredRows.length > 0" class="tbody">
      <DataTableRow
        v-for="(entry, idx) in filteredRows"
        :key="JSON.stringify(entry)"
        :row-data="entry"
        :cols="cols"
        :row-index="idx"
        :colsMinWidths="colsMinWidths"
        :class="{
          'no-frames': noFrames
        }"
        :noFrames="noFrames"
        :selectableRow="selectableRow"
        :filterKey="filterKey"
        @checkRow="verifyCheckeds"
        @rowInteraction="$emit('rowInteraction', $event)"
      >
        <template v-for="(_, slot) in $scopedSlots" v-slot:[slot]="props">
          <slot :name="slot" v-bind="props"></slot>
        </template>
      </DataTableRow>
    </div>
    <div v-else class="tbody">
      <p class="not-found-message">{{ notFoundMessage }}</p>
    </div>
    <!-- / CORPO -->
  </div>
</template>

<script>
/**
 * Componente de datatable
 * TODO - complementar a documentação do componente
 * @event rowInteraction dispara um evento com os dados da linha que recebeu interação do usuário
 * @event getSelectedRows dispara um evento com um array de todas as linhas selecionadas
 */

import DataTableRow from './DataTableRow'
import InputCheckbox from '../elements/InputCheckbox'
import Skeleton from '../elements/Skeleton'

export default {
  name: 'Datatable',
  components: {
    DataTableRow,
    InputCheckbox,
    Skeleton
  },
  props: {
    /**
     * Array de objetos
     * @example {
     *  data: {
     *    fieldName: 'value',
     *    ...
     *  },
     *  details: [
     *    {
     *      fieldName: 'value',
     *      ...
     *    },
     *    ...
     *  ]
     * }
     */
    rows: {
      type: Array,
      default: () => []
    },
    /**
     * Array de objetos
     * @example [
     *  {
     *    name: 'fieldName',
     *    alias: 'th label'
     *  },
     *  ...
     * ]
     */
    cols: {
      type: Array,
      default: () => []
    },
    /**
     * Se deve apresentar cabeçalho
     */
    showHeader: {
      type: Boolean,
      default: true
    },
    /**
     * Termo para ser buscado dentro da tabela
     */
    filterKey: {
      type: String
    },
    /**
     * Define uma largura mínima em px para as colunas especificadas
     * @example [
     *  {
     *    colX: 200,
     *    ...
     *  }
     * ]
     */
    colsMinWidths: {
      type: Object,
      default: () => {}
    },
    /**
     * Se deve esconder as divisões das colunas da tabela
     */
    noFrames: {
      type: Boolean,
      default: false
    },
    /**
     * Se verdadeiro adiciona uma coluna para seleção dos itens
     */
    selectableRow: {
      type: Boolean,
      default: false
    },
    /**
     * Se deve ordenar as colunas ao clicar nos cabeçalhos
     */
    sortable: {
      type: Boolean,
      default: true
    },
    /**
     * Termos usados para se referencias as linhas selecionadas
     */
    selectedsRowsTerms: {
      type: String,
      default: 'linha selecionada | linhas selecionadas'
    },
    /**
     * Altura máxima da listagem
     */
    maxHeight: {
      type: [Number, String]
    },
    /**
     * Colunas para aplicar a busca por texto
     * se não for passado um valor, procura em todas as colunas
     */
    searchableColumns: {
      type: Array,
      default: () => []
    },
    /**
     * Flag para saber se está carregando os dados
     */
    isLoading: {
      type: Boolean,
      default: true
    },
    /** Texto da mensagem de sem conteúdo */
    notFoundMessage: {
      type: String,
      default: 'Não existem itens para serem listados.'
    }
  },
  data() {
    const sortOrders = {}
    this.cols.forEach((key) => {
      sortOrders[key.name] = null
    })
    return {
      tableScrolled: false,
      sortKey: '',
      sortOrders: sortOrders,
      componentKey: new Date().getTime(), // chave única para o componente
      selectedRowsCount: 0 // contador de linhas selecionadas
    }
  },
  computed: {
    filteredRows() {
      const sortKey = this.sortKey
      const filterKey = this.filterKey && this.filterKey.toLowerCase()
      const order = this.sortOrders[sortKey] || 1
      let rows = this.rows
      if (filterKey) {
        rows = rows.filter((row) => {
          return Object.keys(row.data).some((key) => {
            /**
             * Se foram passadas colunas específicas para filtrar
             * testa elas
             */
            if (
              this.searchableColumns.length > 0 &&
              !this.searchableColumns.includes(key)
            ) {
              return false
            }

            return (
              String(row.data[key])
                .toLowerCase()
                .indexOf(filterKey) > -1
            )
          })
        })
      }

      if (sortKey) {
        rows = rows.slice().sort((a, b) => {
          a = isNaN(+a.data[sortKey]) ? a.data[sortKey] : +a.data[sortKey]
          b = isNaN(+b.data[sortKey]) ? b.data[sortKey] : +b.data[sortKey]

          return (a === b ? 0 : a > b ? 1 : -1) * order
        })
      }
      return rows
    }
  },
  watch: {
    rows: {
      handler(newVal) {
        this.selectedRowsCount = newVal.reduce((acc, curr) => {
          acc += Number(curr.data.checked)
          return acc
        }, 0)
      },
      deep: true
    }
  },
  methods: {
    sortBy(key) {
      if (!this.sortable) return

      this.sortKey = key
      if (this.sortOrders[key]) {
        this.sortOrders[key] = this.sortOrders[key] * -1
        return
      }
      this.sortOrders[key] = 1
    },
    /**
     * Seleciona todas as linhas da tabela
     */
    selectAllRows(value) {
      let emitedValue = []

      this.rows.forEach((item) => {
        item.data.checked = value
      })

      if (value) {
        this.selectedRowsCount = this.rows.length

        emitedValue = this.rows.flatMap((item) => item.data)
      } else {
        this.selectedRowsCount = 0
      }

      this.emitSelectedRowsEvent(emitedValue)
    },
    /**
     * gerencia o total de linhas selecionadas
     */
    verifyCheckeds(value) {
      if (value) {
        this.selectedRowsCount += 1
        this.emitSelectedRowsEvent(this.getSelectedRowsValues())
        return
      }

      if (this.selectedRowsCount > 0) {
        this.selectedRowsCount -= 1
      }

      this.emitSelectedRowsEvent(this.getSelectedRowsValues())
    },
    /**
     * retorna um array de objetos com os valores das linhas selecionadas
     */
    getSelectedRowsValues() {
      return this.rows
        .filter((item) => item.data.checked)
        .flatMap((item) => item.data)
    },
    /**
     * Emite um evento com um array das linhas selecionadas
     */
    emitSelectedRowsEvent(value) {
      this.$emit('getSelectedRows', value)
    },
    scrollHandler(e) {
      this.tableScrolled = e.target.scrollTop > 5
    }
  }
}
</script>

<style lang="scss" scoped>
$head-color: $gray-darkest;
$border-color: $gray-border;
$bg-row-stripe: $gray-lightest;

.datable-grid-wrapper {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 414px;
  min-width: 100px;
  border: 1px solid $border-color;
  border-top: none;
  @include scrollbars(5px, $border-color, #fff);
}

.thead {
  position: sticky;
  top: 0;
  z-index: 1;
  color: $head-color;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  flex-wrap: nowrap;

  .th {
    display: flex;
    background-color: #fff;
    justify-content: space-between;
    align-items: center;
    padding: 15px 14px;
    flex: 1 1 auto;
    min-width: 100px;
    border: 1px solid $border-color;
    border-left: none;

    &:last-child {
      border-right: none;
    }

    .icon {
      color: $primary;
    }

    .label {
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      @include line-overflow(12px, 12px, 1);
    }

    &--selection {
      max-width: 48px;
      min-width: 48px;
    }
  }
}

.no-frames {
  &.datable-grid-wrapper {
    border: none;
  }

  .thead {
    transition: filter $color-anim;

    &.scrolled {
      @include drop-shadow(0px 5px 10px rgba(0, 0, 0, 0.1));
    }

    .th {
      border: none;
      justify-content: center;
      text-align: center;

      .label {
        @include line-overflow(14px, 14px, 2);
      }

      &:first-child {
        justify-content: flex-start;
        text-align: left;
      }
    }
  }

  .tbody {
    .tr-wrapper {
      @include box-shadow(0 0 0 rgba(0, 0, 0, 0.1));
      transition: box-shadow $color-anim linear;

      &:hover {
        @include box-shadow(0 8px 25px rgba(0, 0, 0, 0.1));
      }
    }
  }

  &.has-row-selection {
    .thead {
      .th {
        &:nth-child(2) {
          justify-content: flex-start;
          text-align: left;
        }
      }
    }
  }
}

.tbody {
  display: flex;
  flex-wrap: wrap;
  border-top: none;

  & ::v-deep .details {
    .tbody {
      border: none;

      .tr-wrapper {
        background-color: inherit;
        &:nth-child(odd) {
          .td {
            background: none;
          }
        }
      }

      .td {
        padding-left: 45px;
      }
    }
  }

  .not-found-message {
    margin: 25px 50px;
    color: $secondary;
    font-size: font-size('size-small');
    font-style: italic;
  }
}

.skeleton {
  &-wrapper {
    width: 100%;
  }
  &-row {
    display: flex;
  }

  &-col {
    min-width: 100px;
    display: flex;
    align-items: center;
    font-size: 11px;
    padding: 10px 10px;
    flex: 1 1 auto;
    justify-content: center;

    &--selection {
      max-width: 48px;
      min-width: 48px;
    }
  }
}
</style>
